<script setup>
/// <reference path="charting_library.d.ts" />
import { get, set, tryOnBeforeUnmount, tryOnMounted, useOnline, usePageLeave, useStorage, watchDebounced, watchThrottled } from '@vueuse/core'
import { widget } from './charting_library.esm'
import { useStore } from '~/store'
import { useSpotStore } from '~/store/spot'
import ws, { kline, timestamp } from '~/apis/spotSocket'
import { queryKlineList } from '~/apis/home'

const props = defineProps({
  resolution: {
    type: [Number, String],
    default: '',
  },
})

const { locale } = useI18n()
const spot = useSpotStore()
const store = useStore()
const isOnline = useOnline()
const isLeft = usePageLeave()

let tv
const timezone = useStorage('ttx-tvTimezone-spot', Intl.DateTimeFormat().resolvedOptions().timeZone)
const ranges4ws = {
  '0': 'min_1',
  '1': 'min_1',
  '5': 'min_5',
  '15': 'min_15',
  '30': 'min_30',
  '60': 'hour_1',
  '240': 'hour_4',
  '720': 'hour_12',
  '1D': 'day_1',
  '1W': 'week_1',
}

const kv = {}
/** @type {TradingView.IBasicDataFeed} */

class Datafeed {
  onReady(cb) {
    const params = {
      symbol: spot.code,
      type: 'crypto',
      supported_resolutions: Object.keys(ranges4ws),
      supports_time: true,
    }
    setTimeout(() => {
      cb(params)
    })
  }

  searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
    if (store.contracts) {
      onResultReadyCallback(store.contracts.map(i => ({
        symbol: i.code,
        full_name: i.displayName,
        ticker: i.code,
        description: store.currenciesRaw[i.base]?.describe || '...',
        exchange: 'tt',
        type: 'crypto',
      })))
    }
  }

  resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback, extension) {
    setTimeout(() => {
      onSymbolResolvedCallback({
        symbol: spot.code,
        isSpot: false,
        exchange: 'tt', // TODO
        listed_exchange: 'tt',
        name: spot.curInfo.displayName,
        pricescale: Number(`1e${spot.curInfo.ps}`),
        volume_precision: spot.curInfo.as,
        timezone: get(timezone),
        minmov: 1,
        type: 'crypto',
        session: '24x7', // 0000-2400:2345671;1
        has_daily: true,
        has_intraday: true,
        has_weekly_and_monthly: true,
      })
    })
  }

  getBars(symbolInfo, resolution, periodParams, onResult, onError) {
    const { countBack, to } = periodParams

    let period = ranges4ws[resolution].split('_').reverse().join('')
    period === '1hour' && (period = '60min')
    const fn = () => queryKlineList({ symbol: spot.code, period, endTime: to * 1000, size: countBack, fillLast: false }).then(({ success, data, message }) => {
      if (success) {
        const { item = [] } = data[0] ?? {}
        onResult(item.map(i => ({
          time: +i.startTime * 1000,
          open: +i.open,
          high: +i.high,
          low: +i.low,
          close: +i.close,
          volume: +i.volume,
        })), { noData: item.length < countBack })
      }
      else {
        onError(message)
      }
    }).catch((e) => {
      console.log('getBar error', e)
      setTimeout(() => fn(), 5000)
    })
    fn()
  }

  subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback) {
    const range = ranges4ws[resolution]
    kv[listenerGuid] = [
      watch(kline, (i) => {
        if (i && i.symbol === spot.code && i.interval === range) {
          onTick({
            time: +i.startTime * 1000,
            open: +i.open,
            high: +i.high,
            low: +i.low,
            close: +i.close,
            volume: +i.volume,
          })
        }
      }),
      watchDebounced(isOnline, (v1, oldv1) => {
        if (v1 && !oldv1) {
          onResetCacheNeededCallback()
          tv.activeChart().resetData()
        }
      }, {
        debounce: 500,
      }),
    ]
  }

  unsubscribeBars(listenerGuid) {
    // console.log('@unsubscribeBars > listenerGuid', listenerGuid)
    const [code, range] = listenerGuid.split('_#_')
    unsubData(spot.getInfo(code).id, ranges4ws[range])

    if (Array.isArray(kv[listenerGuid])) {
      kv[listenerGuid].forEach(stop => stop())
      kv[listenerGuid] = undefined
    }
  }

  getTimescaleMarks() {

  }

  getServerTime(cb) {
    cb(Math.floor(get(timestamp) / 1000))
  }

  getVolumeProfileResolutionForPeriod() {

  }
}

/** @type {TradingView.IChartingLibraryWidget} */
const dom = ref()

function generateTheme() {
  // @TODO 先写死，否则闪烁
  const css = getComputedStyle(document.documentElement)
  const bg = '#ffffff' || css.getPropertyValue('--fillPagePrimary').trim()
  const rise = '#01bc8d' || css.getPropertyValue('--buy').trim()
  const fall = '#f1493f' || css.getPropertyValue('--sell').trim()
  const t2 = '#717171' || css.getPropertyValue('--textTextTertiary').trim()
  return {
    bg,
    rise,
    fall,
    'paneProperties.backgroundType': 'solid',
    'paneProperties.background': bg,
    'paneProperties.backgroundGradientStartColor': bg,
    'paneProperties.backgroundGradientEndColor': bg,
    'mainSeriesProperties.candleStyle.upColor': rise,
    'mainSeriesProperties.candleStyle.downColor': fall,
    'mainSeriesProperties.candleStyle.borderUpColor': rise,
    'mainSeriesProperties.candleStyle.borderDownColor': fall,
    'mainSeriesProperties.candleStyle.wickUpColor': rise,
    'mainSeriesProperties.candleStyle.wickDownColor': fall,
    'mainSeriesProperties.lineStyle.color': rise,
    'scalesProperties.textColor': t2,
  }
}
function syncTheme() {
  tv?.changeTheme('Light').then(() => {
    const theme = generateTheme()
    tv.applyOverrides(theme)
    tv.applyStudiesOverrides({
      'volume.volume.color.0': theme.fall,
      'volume.volume.color.1': theme.rise,
    })
  })
}
function syncThemeSafe() {
  nextTick(() => {
    try {
      syncTheme()
    }
    catch (e) {
      tv?.onChartReady(syncTheme)
    }
  })
}
/**
 * caveats
 *
 * tv的主题切换功能有一些神奇的bug, 为了解决黑白天切换后下次刷新页面黑色背景颜色不对的问题，需要在tv加载前修改掉本地据的数来保证颜色的正确性。
 * 比较鸡贼的方案，不建议使用，但是能用。
 */
function fixTradingViewLocalData() {
  const key = 'tradingview.chartproperties'
  const lsData = localStorage.getItem(key)
  if (lsData) {
    try {
      const json = JSON.parse(lsData)
      const theme = generateTheme()
      json.paneProperties.backgroundType = 'solid'
      json.paneProperties.background = theme['paneProperties.background']
      json.paneProperties.backgroundGradientStartColor = theme['paneProperties.backgroundGradientStartColor']
      json.paneProperties.backgroundGradientEndColor = theme['paneProperties.backgroundGradientEndColor']
      localStorage.setItem(key, JSON.stringify(json))
    }
    catch (_) {
      // no catch
    }
  }
}
const getResolution = resolution => resolution === '0' ? 1 : resolution
const getChartType = resolution => resolution === '0' ? 2 : 1
function setTradingviewSymbol(symbol) {
  try {
    if (tv && tv._innerAPI && tv.activeChart())
      tv.activeChart().setSymbol(symbol)
  }
  catch (_) {
    //
  }
}
function initTV() {
  const theme = generateTheme()
  const { getLastChart, initAutoSave } = useAutoSave()
  const savedData = getLastChart()

  fixTradingViewLocalData()
  /* eslint-disable new-cap */
  tv = new widget({
    symbol: spot.code,
    timezone: get(timezone),
    locale: getLocale(),
    interval: getResolution(props.resolution),
    container: 'tv',
    datafeed: new Datafeed(),
    width: '100%',
    height: '100%',
    load_last_chart: true,
    saved_data: savedData,
    library_path: '/charting_library/',
    custom_css_url: 'custom.css',
    numeric_formatting: { decimal_sign: (0).toLocaleString('en-us', { minimumFractionDigits: 1 }).replace(/0/g, '') },
    loading_screen: {
      backgroundColor: theme.bg,
      foregroundColor: '#02AA99',
    },
    disabled_features: [
      'timeframes_toolbar',
      'compare_symbol',
      'volume_force_overlay',
      'header_symbol_search',
      'header_widget',
      'timezone_menu',
    ],
    enabled_features: [
      'hide_last_na_study_output',
      'move_logo_to_main_pane',
    ],
    overrides: {
      ...theme,
      'volumePaneSize': 'small',
      'mainSeriesProperties.candleStyle.barColorsOnPrevClose': false,
    },
    studies_overrides: {
      'volume.volume.color.0': theme.fall,
      'volume.volume.color.1': theme.rise,
    },
    theme: 'Light',
  })
  // tv.headerReady().then(syncTheme);
  const mas = [
    {
      length: 5,
      color: '#965fc4',
    },
    {
      length: 10,
      color: '#84aad5',
    },
    {
      length: 30,
      color: '#b7248a',
    },
  ]
  tv.onChartReady(() => {
    initAutoSave()
    syncTheme()
    setTradingviewSymbol(spot.code)
    /** @type {TradingView.IChartWidgetApi} */
    const chart = tv.activeChart()
    chart.setChartType(getChartType(props.resolution))
    chart.getTimezoneApi().onTimezoneChanged().subscribe(null, (t) => {
      set(timezone, t)
    })
    if (!savedData) {
      mas.forEach((i) => {
        chart.createStudy(
          'Moving Average',
          true,
          false,
          { length: i.length },
          {
            'plot.color': i.color,
            'showLabelsOnPriceScale': false,
          },
        ).then(() => {})
      })
    }
  })
  tv.subscribe('onScreenshotReady', (data) => {
    window.open(`https://www.tradingview.com/x/${data}`, '_blank')
  })
  if (localStorage.debug)
    window.tv = tv
}

function useAutoSave() {
  const saved = useStorage('ttx-tvAutoSave', {}, undefined, { listenToStorageChanges: false })

  const saving = (data) => {
    saved.value = data
  }
  const getLastChart = () => {
    if (saved.value.layout) {
      saved.value.charts[0].panes[0].sources[0].state.interval = getResolution(props.resolution)
      saved.value.charts[0].panes[0].sources[0].state.symbol = spot.code
      return saved.value
    }
  }
  const initAutoSave = () => {
    tv.subscribe('onAutoSaveNeeded', () => tv.save(saving))
    // tv.activeChart().onChartTypeChanged().subscribe(null, (type) => {
    //   if ([1, 2].includes(type)) {
    //     return
    //   }
    //   chartStore.chartType = type
    // })
  }

  return {
    saved,
    initAutoSave,
    getLastChart,
  }
}

function execTradingviewAction(actionId) {
  const chart = tv?.activeChart()
  if (chart) {
    tv.closePopupsAndDialogs()
    chart.executeActionById(actionId)
  }
}

const screenShot = () => tv?.takeScreenshot()
const toggleSetting = () => execTradingviewAction('chartProperties')
const toggleIndicator = () => execTradingviewAction('insertIndicator')
function subData() {
  ws.subscribe(`${spot.id}@kline@${ranges4ws[props.resolution]}`)
}

function unsubData(id, resolution) {
  ws.unsubscribe(`${id ?? spot.id}@kline@${resolution ?? ranges4ws[props.resolution]}`)
}

function getLocale() {
  const o = { 'zh-hant': 'zh_TW', 'hi': 'en', 'km': 'en', 'id': 'id_ID' }
  const [lang = 'en'] = locale.value.split('-') ?? []
  return o[locale.value] || lang
}

tryOnMounted(() => {
  watch(() => spot.code, (code, ov) => {
    if (code) {
      subData()
      if (tv)
        setTradingviewSymbol(code)
      else
        initTV()
    }
  }, {
    immediate: true,
  })
  watch(locale, () => {
    initTV()
  })
  watch(() => props.resolution, (resolution, ov) => {
    subData()
    const chart = tv?.activeChart()
    if (chart) {
      chart.setResolution(getResolution(resolution))
      chart.setChartType(getChartType(resolution))
    }
  })
  watchThrottled(isLeft, (v, oldv) => {
    if (!v && oldv) {
      try {
        tv?._innerAPI().activeChart()
      }
      catch (e) {
        if (get(dom)) {
          get(dom).innerHTML = ''
          initTV()
        }
      }
    }
  }, {
    throttle: 2000,
  })
})
tryOnBeforeUnmount(() => {
  tv?.remove()
})

defineExpose({
  screenShot,
  toggleSetting,
  toggleIndicator,
})
</script>

<template>
  <div id="tv" ref="dom" class="w-full h-full" />
</template>
